import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Code, Menu, X } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { NAV_ITEMS } from "../data/portfolioData";

export const Navigation = ({ coins, onCoinCollect, onNavigate }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavigate = (id) => {
    setMobileOpen(false);
    onNavigate(id);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100 }}
      className="sticky top-0 z-50 bg-[hsl(var(--card))]/80 backdrop-blur-xl border-b border-[hsl(var(--border))] shadow-lg"
      data-testid="header"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-10 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
              className="w-10 h-10 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] rounded-lg flex items-center justify-center shadow-lg"
              data-testid="logo"
            >
              <Code className="w-5 h-5 text-[hsl(var(--primary-foreground))]" />
            </motion.div>
            <span className="font-display text-lg font-bold tracking-tight" data-testid="logo-text">
              Ryan M Colon
            </span>
          </div>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className="text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onCoinCollect}
              className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/20 rounded-lg cursor-pointer hover:bg-[hsl(var(--primary))]/20 transition-all glow"
              data-testid="coin-counter"
            >
              <div className="w-2 h-2 bg-[hsl(var(--primary))] rounded-full animate-pulse" />
              <span className="font-mono text-sm font-semibold text-[hsl(var(--primary))]">{coins}</span>
            </motion.div>

            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="md:hidden w-10 h-10 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/50 flex items-center justify-center transition-all"
              data-testid="mobile-menu-toggle"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-[hsl(var(--foreground))]" />
              ) : (
                <Menu className="w-5 h-5 text-[hsl(var(--foreground))]" />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-[hsl(var(--border))]"
              data-testid="mobile-nav"
            >
              <div className="flex flex-col py-4 gap-1">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    className="text-left px-4 py-3 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors"
                    data-testid={`mobile-nav-${item.id}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
